import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useVoiceStore } from '@stores/voiceStore';

interface VoiceContextValue {
  isSupported: boolean;
  isListening: boolean;
  isSpeaking: boolean;
  transcript: string;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
  speak: (text: string) => Promise<void>;
  stopSpeaking: () => void;
  clearError: () => void;
}

const VoiceContext = createContext<VoiceContextValue | undefined>(undefined);

interface VoiceProviderProps {
  children: ReactNode;
}

// Helper function to safely convert error to string
const errorToString = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object') {
    // Handle SpeechRecognitionErrorEvent and SpeechSynthesisErrorEvent
    if ('error' in error) {
      const errorValue = (error as { error: unknown }).error;
      if (typeof errorValue === 'string') {
        return errorValue;
      }
    }
    if ('message' in error) {
      const messageValue = (error as { message: unknown }).message;
      if (typeof messageValue === 'string') {
        return messageValue;
      }
    }
    // Try to stringify, but handle circular references
    try {
      return JSON.stringify(error);
    } catch {
      return 'Errore sconosciuto';
    }
  }
  return String(error);
};

export function VoiceProvider({ children }: VoiceProviderProps) {
  const [isSupported, setIsSupported] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const [synthesis] = useState<SpeechSynthesis | null>(
    typeof window !== 'undefined' ? window.speechSynthesis : null
  );

  const {
    isListening,
    isSpeaking,
    transcript,
    error,
    setIsListening,
    setIsSpeaking,
    setTranscript,
    setError,
    language,
  } = useVoiceStore();

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition && synthesis) {
      setIsSupported(true);
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = language;

      recognitionInstance.onstart = () => {
        setIsListening(true);
        setError(null);
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
      };

      recognitionInstance.onresult = (event) => {
        const results = Array.from(event.results);
        const transcript = results
          .map((result) => result[0].transcript)
          .join('');
        setTranscript(transcript);
      };

      recognitionInstance.onerror = (event: SpeechRecognitionErrorEvent) => {
        // Safely convert error to string
        const errorMessage = errorToString(event.error);
        console.error('Speech recognition error:', errorMessage);
        setError(`Errore riconoscimento: ${errorMessage}`);
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
    }
  }, [language, setError, setIsListening, setTranscript, synthesis]);

  // Update language when it changes
  useEffect(() => {
    if (recognition) {
      recognition.lang = language;
    }
  }, [language, recognition]);

  const startListening = useCallback(() => {
    if (recognition && !isListening) {
      try {
        setTranscript('');
        recognition.start();
      } catch (err) {
        console.error('Failed to start recognition:', err);
        setError('Impossibile avviare il riconoscimento vocale');
      }
    }
  }, [recognition, isListening, setTranscript, setError]);

  const stopListening = useCallback(() => {
    if (recognition && isListening) {
      recognition.stop();
    }
  }, [recognition, isListening]);

  const speak = useCallback(
    async (text: string): Promise<void> => {
      if (!synthesis) {
        setError('Sintesi vocale non supportata');
        return;
      }

      // Stop any current speech
      synthesis.cancel();

      return new Promise((resolve, reject) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = language;
        utterance.rate = 1.0;
        utterance.pitch = 1.0;

        utterance.onstart = () => {
          setIsSpeaking(true);
        };

        utterance.onend = () => {
          setIsSpeaking(false);
          resolve();
        };

        utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
          setIsSpeaking(false);
          // Safely convert error to string
          const errorMessage = errorToString(event.error);
          setError(`Errore sintesi vocale: ${errorMessage}`);
          reject(new Error(errorMessage));
        };

        synthesis.speak(utterance);
      });
    },
    [synthesis, language, setIsSpeaking, setError]
  );

  const stopSpeaking = useCallback(() => {
    if (synthesis) {
      synthesis.cancel();
      setIsSpeaking(false);
    }
  }, [synthesis, setIsSpeaking]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  const value: VoiceContextValue = {
    isSupported,
    isListening,
    isSpeaking,
    transcript,
    error,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    clearError,
  };

  return (
    <VoiceContext.Provider value={value}>
      {children}
    </VoiceContext.Provider>
  );
}

export function useVoiceContext() {
  const context = useContext(VoiceContext);
  if (context === undefined) {
    throw new Error('useVoiceContext must be used within a VoiceProvider');
  }
  return context;
}
